import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CallIcon from '@mui/icons-material/Call';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/authContext';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import useCustomerExperienceFeedback from '../../hooks/useCustomerExperienceFeedback';
import useFeedbackClassification from '../../hooks/useFeedbackClassification';
import useEntertainment from '../../hooks/useEntertainment';
import useEntertainmentType from '../../hooks/useEntertainmentType';
import useUser from '../../hooks/useUser';
import { currencyFormatter } from '../../util/formatHelper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { getFeedbackDeadline, formatFeedbackAge, getEscalationStage } from '../../util/feedbackDeadlines';

const CLASSIFICATION_SOURCE_LABELS = {
  ai: 'KI',
  manual: 'Manuell',
  customer: 'Kunde',
};

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'error', Icon: ErrorOutlineIcon },
  inProgress: { label: 'In Bearbeitung', color: 'warning', Icon: HourglassEmptyIcon },
  resolved: { label: 'Gelöst', color: 'success', Icon: CheckCircleIcon },
};

const VOUCHER_PRESETS = [10, 20, 30, 50];
const SYSTEM_DEFAULT_VOUCHER_LIMIT = 50;
/** Hard system ceiling for a goodwill voucher, independent of the entertainment's approval limit. */
const MAX_COURTESY_VOUCHER_AMOUNT = 100;

/** A goodwill amount is valid when it is a number above 0 and at most the system ceiling. */
const isValidVoucherAmount = raw => {
  const value = Number(String(raw).trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 && value <= MAX_COURTESY_VOUCHER_AMOUNT;
};

const APPROVAL_REASON_LABELS = {
  limit: 'über dem Gutschein-Limit',
  budget: 'über dem Monatsbudget',
};

// Who has to act on a waiting request — named in plain German, with the right in
// brackets so an admin can be asked for it.
const APPROVAL_DECIDER_LABELS = {
  limit: 'Berechtigte für hohe Kulanzgutscheine (Recht „approveHighValueVoucher“)',
  budget: 'Budgetverantwortliche (Recht „giftedBalanceApprove“)',
};

const formatEuro = value =>
  Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const describeApprovalReasons = (reasons = []) =>
  reasons.map(reason => APPROVAL_REASON_LABELS[reason] || reason).join(' und ');

const describeApprovalDeciders = (reasons = []) =>
  reasons.map(reason => APPROVAL_DECIDER_LABELS[reason] || reason).join(' und ');

const describePerson = person =>
  person?.username ? person.username : person?._id ? 'einem Kollegen' : '';

/**
 * The life of an approval request as a short vertical timeline: who asked for
 * what, who has to decide, and what happens next. Without this the employee only
 * sees that "something" is pending and has no idea where their answer went.
 */
function ApprovalStatusTimeline({ request, formatDate }) {
  const isPending = request.status === 'pending';
  const isApproved = request.status === 'approved';
  const isRejected = request.status === 'rejected';
  const isObsolete = request.status === 'obsolete';

  const steps = [
    {
      key: 'requested',
      done: true,
      label: 'Freigabe angefragt',
      detail: [
        `${formatEuro(request.amount)} € Kulanzgutschein (${describeApprovalReasons(request.reasons)})`,
        describePerson(request.requestedBy) ? `von ${describePerson(request.requestedBy)}` : '',
        request.requestedAt ? `am ${formatDate(request.requestedAt)}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    },
    {
      key: 'decision',
      done: !isPending,
      pending: isPending,
      label: isPending
        ? 'Wartet auf Freigabe'
        : isApproved
        ? 'Freigegeben'
        : isRejected
        ? 'Abgelehnt'
        : 'Hinfällig',
      detail: isPending
        ? `Zuständig: ${describeApprovalDeciders(request.reasons)}`
        : [
            describePerson(request.decidedBy) ? `von ${describePerson(request.decidedBy)}` : '',
            request.decidedAt ? `am ${formatDate(request.decidedAt)}` : '',
            request.decisionComment ? `Grund: ${request.decisionComment}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
    },
    {
      key: 'result',
      done: isApproved,
      label: isApproved ? 'Antwort gesendet' : 'Antwort geht nach der Freigabe automatisch raus',
      detail: isApproved
        ? request.createdVoucherCode
          ? `Gutschein ${request.createdVoucherCode} wurde erstellt und der Mail angehängt`
          : 'Gutschein wurde erstellt und der Mail angehängt'
        : isRejected
        ? 'Nichts wurde gesendet – du kannst die Antwort anpassen und erneut senden.'
        : isObsolete
        ? 'Das Feedback wurde zwischenzeitlich anders beantwortet – es ging nichts doppelt raus.'
        : 'Du musst nichts erneut schreiben.',
    },
  ];

  return (
    <Box sx={{ mt: 0.5 }}>
      {steps.map((step, index) => (
        <Box key={step.key} sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0,
              pt: 0.5,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: step.done || step.pending ? 'info.main' : 'divider',
                bgcolor: step.done ? 'info.main' : 'transparent',
              }}
            />
            {index < steps.length - 1 && (
              <Box sx={{ width: '2px', flex: 1, minHeight: 18, bgcolor: 'divider' }} />
            )}
          </Box>
          <Box sx={{ pb: index < steps.length - 1 ? 1 : 0, minWidth: 0 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: step.pending ? 700 : 500, lineHeight: 1.4 }}
            >
              {step.label}
            </Typography>
            {step.detail && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {step.detail}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Standard response template used when the customer left no written message.
 * Uses the entertainment name for personalisation.
 */
function buildNoMessageStandardText(firstName, entertainmentName) {
  const name = firstName ? `Hallo ${firstName},` : 'Hallo,';
  const ent = entertainmentName || 'uns';
  return `${name}

danke, dass du dir die Zeit genommen hast, Feedback zu deinem Besuch in der ${ent} zu geben.

Was hat dich gestört oder nicht gestimmt? Schreib uns gerne kurz – wir kümmern uns darum.`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function redactEmail(email) {
  if (!email) return '—';
  const at = email.indexOf('@');
  if (at < 1) return email;
  const local = email.substring(0, at);
  const domain = email.substring(at);
  const visible = Math.min(2, local.length);
  return local.substring(0, visible) + '***' + domain;
}

function CallNoteDialog({ open, onClose, onConfirm, loading }) {
  const noteRef = useRef(null);

  const handleConfirm = useCallback(() => {
    const note = noteRef.current?.value?.trim() || '';
    onConfirm(note);
  }, [onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Anruf dokumentieren</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Halte kurz fest, was im Gespräch kommuniziert wurde.
        </Typography>
        <TextField
          inputRef={noteRef}
          label='Gesprächsnotiz (optional)'
          multiline
          minRows={3}
          fullWidth
          placeholder='z.B. Kunde ist zufrieden mit Lösung, erhält Gutschein per Mail...'
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          variant='outlined'
          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
          onClick={handleConfirm}
          disabled={loading}
        >
          Als angerufen markieren
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Small note dialog shown when a reviewer rejects the AI's auto-resolve
 * assessment. The note is optional and helps admins understand later why the AI
 * was judged wrong.
 */
function RejectTriageDialog({ open, onClose, onConfirm, loading }) {
  const noteRef = useRef(null);

  const handleConfirm = useCallback(() => {
    const note = noteRef.current?.value?.trim() || '';
    onConfirm(note);
  }, [onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>KI-Einschätzung ablehnen</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Die KI hielt diesen Fall für automatisch beantwortbar. Wenn das nicht stimmt, wird der
          Fall normal manuell bearbeitet. Halte optional fest, warum die Einschätzung falsch war –
          das hilft, die Treffsicherheit der KI zu bewerten.
        </Typography>
        <TextField
          inputRef={noteRef}
          label='Begründung (optional)'
          multiline
          minRows={3}
          fullWidth
          placeholder='z.B. Sachlage unklar, Vorwurf zu schwerwiegend, Gutschein unpassend...'
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='error'
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <CloseIcon />}
          onClick={handleConfirm}
          disabled={loading}
        >
          Ablehnen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Rejects a parked answer (or withdraws one's own request). The reason is shown
 * to the employee who wrote the answer, so they know how to proceed.
 */
function RejectApprovalDialog({ open, onClose, onConfirm, loading, isWithdrawal }) {
  const commentRef = useRef(null);

  const handleConfirm = useCallback(() => {
    const comment = commentRef.current?.value?.trim() || '';
    onConfirm(comment);
  }, [onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isWithdrawal ? 'Freigabe-Anfrage zurückziehen' : 'Gutschein-Freigabe ablehnen'}
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {isWithdrawal
            ? 'Die Anfrage wird zurückgezogen, das reservierte Budget wieder freigegeben und das Feedback bleibt offen. Du kannst danach eine andere Antwort senden.'
            : 'Die Antwort wird nicht gesendet, das reservierte Budget wird wieder frei und das Feedback bleibt offen. Die Begründung sieht der Mitarbeiter, der die Antwort geschrieben hat.'}
        </Typography>
        <TextField
          inputRef={commentRef}
          label='Begründung (optional)'
          multiline
          minRows={3}
          fullWidth
          placeholder='z.B. Betrag zu hoch, bitte 10 € anbieten...'
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='error'
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <CloseIcon />}
          onClick={handleConfirm}
          disabled={loading}
        >
          {isWithdrawal ? 'Zurückziehen' : 'Ablehnen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Admin-only dialog listing feedback whose AI auto-resolve assessment was
 * rejected by staff. Used to audit how accurate the AI's auto-resolve judgement
 * is. Gated behind the customerExperienceFeedback:special:reviewAiTriage right.
 */
function AiTriageReviewDialog({ open, onClose, items, loading, onSelect }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Abgelehnte KI-Einschätzungen</DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Die KI hielt diese Fälle für automatisch beantwortbar, ein Mitarbeiter hat das aber
          abgelehnt. Prüfe hier, ob die Einschätzung der KI tatsächlich falsch war.
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
            Keine abgelehnten Einschätzungen.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map(item => (
              <Paper key={item._id} variant='outlined' sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='subtitle2'>
                      {item.customerFirstName || 'Kunde'}{' '}
                      <Typography component='span' variant='caption' color='text.secondary'>
                        · {item.entertainmentId?.longName || item.entertainmentId?.entertainmentName || '—'}
                      </Typography>
                    </Typography>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Abgelehnt: {formatDate(item.aiTriage?.decidedAt)}
                    </Typography>
                  </Box>
                  {onSelect && (
                    <Button size='small' variant='outlined' onClick={() => onSelect(item)}>
                      Öffnen
                    </Button>
                  )}
                </Box>
                {item.message && (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.75, fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    „{item.message}“
                  </Typography>
                )}
                {item.aiSuggestion?.autoResolveReason && (
                  <Typography variant='caption' color='primary.main' display='block' sx={{ mt: 0.5 }}>
                    KI-Begründung: {item.aiSuggestion.autoResolveReason}
                  </Typography>
                )}
                {item.aiTriage?.rejectionNote && (
                  <Typography variant='caption' color='error.main' display='block' sx={{ mt: 0.25 }}>
                    Ablehnungsgrund: {item.aiTriage.rejectionNote}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

// The case an approver mail deep-linked to: `?feedbackId=…&approvals=1` opens the
// approval inbox with that feedback selected.
function readLinkedFeedback() {
  if (typeof window === 'undefined') return { feedbackId: '', approvals: false };
  const params = new URLSearchParams(window.location.search);
  const approvals = (params.get('approvals') || '').trim().toLowerCase();
  return {
    feedbackId: (params.get('feedbackId') || '').trim(),
    approvals: approvals === '1' || approvals === 'true',
  };
}

export default function CustomerExperienceFeedbackView() {
  const authCtx = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    getCustomerFeedback,
    updateStatus,
    updateCustomerCalled,
    generateAiSuggestion,
    sendResponse,
    acceptAiTriage,
    rejectAiTriage,
    getRejectedAiTriage,
    setClassification,
    getCourtesyVoucherBudget,
    approveVoucherRequest,
    rejectVoucherRequest,
  } = useCustomerExperienceFeedback();
  const { getCategories } = useFeedbackClassification();
  const { getEntertainments } = useEntertainment();
  const { getEntertainmentTypes, updateAiPrompt: saveAiPrompt } = useEntertainmentType();
  const { getMyBranches } = useUser();

  const canRead = useMemo(
    () =>
      authCtx.checkRight('customerExperienceFeedback:read') ||
      authCtx.checkRight('customerExperienceFeedback:read:own'),
    [authCtx]
  );
  const canRespond = useMemo(
    () =>
      authCtx.checkRight('customerExperienceFeedback:respond') ||
      authCtx.checkRight('customerExperienceFeedback:respond:own'),
    [authCtx]
  );
  const canEditAiPrompt = useMemo(
    () => authCtx.checkRight('entertainmentType:update'),
    [authCtx]
  );
  const canApproveHighValueVoucher = useMemo(
    () => authCtx.checkRight('customerExperienceFeedback:special:approveHighValueVoucher'),
    [authCtx]
  );
  const canReviewAiTriage = useMemo(
    () => authCtx.checkRight('customerExperienceFeedback:special:reviewAiTriage'),
    [authCtx]
  );
  // Owner of the branch goodwill budget — may send an answer that exceeds the
  // remaining monthly budget and may approve requests raised for that reason.
  const canApproveBudget = useMemo(
    () => authCtx.checkRight('voucher:special:giftedBalanceApprove'),
    [authCtx]
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('new');
  const [entertainmentFilter, setEntertainmentFilter] = useState('');
  const [entertainments, setEntertainments] = useState([]);
  const [assignedBranchIds, setAssignedBranchIds] = useState(null); // null = not yet loaded
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingItemId, setAiLoadingItemId] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [calledLoading, setCalledLoading] = useState(false);

  const [callNoteDialogOpen, setCallNoteDialogOpen] = useState(false);
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [bookingModalId, setBookingModalId] = useState(null);

  // AI triage decision state
  const [triageLoading, setTriageLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);

  // Active classification categories for the inline override dropdown
  const [categories, setCategories] = useState([]);
  const [classificationLoading, setClassificationLoading] = useState(false);

  // Ticking clock so the deadline/warning-level chips re-evaluate over time
  // without a manual refresh. One minute granularity is plenty for these bands.
  const [nowTs, setNowTs] = useState(() => Date.now());

  const [voucherValue, setVoucherValue] = useState('');
  const [customVoucherValue, setCustomVoucherValue] = useState('');
  const [selfApprovalConfirmed, setSelfApprovalConfirmed] = useState(false);
  const [responseDisplay, setResponseDisplay] = useState('');

  // Monthly goodwill budget of the selected feedback's branch
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  // Only show answers that are waiting for a voucher approval. The approver mail
  // links here as `?feedbackId=…&approvals=1`, so the view opens on that inbox
  // instead of the default "Neu" tab, where a parked answer is not listed at all.
  const [linkedFeedback] = useState(readLinkedFeedback);
  const [approvalFilter, setApprovalFilter] = useState(linkedFeedback.approvals);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectApprovalDialogOpen, setRejectApprovalDialogOpen] = useState(false);

  // AI prompt editor state
  const [entertainmentTypes, setEntertainmentTypes] = useState([]);
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiPromptSaving, setAiPromptSaving] = useState(false);
  const [aiPromptTypeId, setAiPromptTypeId] = useState('');
  const aiPromptRef = useRef(null);

  const responseRef = useRef(null);
  const didMount = useRef(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(33);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // 100 € is the hard system ceiling for a goodwill voucher; the entertainment limit only decides
  // whether an approval is needed. An entry outside the ceiling is rejected rather than silently
  // corrected, so nobody sends an amount they did not type.
  const customVoucherError =
    voucherValue === 'custom' &&
    customVoucherValue.trim() !== '' &&
    !isValidVoucherAmount(customVoucherValue)
      ? `Bitte einen Betrag zwischen 1 und ${MAX_COURTESY_VOUCHER_AMOUNT} € eingeben.`
      : null;

  // Computed voucher limit state — derived outside JSX so the send button can gate on them
  const resolvedVoucherNum =
    voucherValue === 'custom'
      ? Number(customVoucherValue) || 0
      : voucherValue ? Number(voucherValue) : 0;
  const effectiveVoucherLimit =
    selected?.entertainmentId?.maxCourtesyVoucherAmount ?? SYSTEM_DEFAULT_VOUCHER_LIMIT;
  const isVoucherAboveLimit = resolvedVoucherNum > 0 && resolvedVoucherNum > effectiveVoucherLimit;

  const pendingApproval = selected?.pendingApproval || null;
  const lastApproval = selected?.lastApproval || null;
  // The employee who raised a request may withdraw it even without approval rights.
  const isOwnApprovalRequest =
    !!pendingApproval &&
    String(pendingApproval.requestedBy?._id || '') === String(authCtx.currentUser?.userId || '');

  const budgetRemaining = Number(budget?.remaining ?? 0);
  const isVoucherAboveBudget =
    !!budget && resolvedVoucherNum > 0 && resolvedVoucherNum > budgetRemaining;

  // Splits what the current amount exceeds into what this user may wave through
  // themselves and what needs someone else's decision. The send button follows
  // this: nothing to clear → send, own rights suffice → confirm and send,
  // otherwise → park the answer for approval.
  const { selfApprovalReasons, approvalReasons } = useMemo(() => {
    const own = [];
    const foreign = [];
    if (isVoucherAboveLimit) (canApproveHighValueVoucher ? own : foreign).push('limit');
    if (isVoucherAboveBudget) (canApproveBudget ? own : foreign).push('budget');
    return { selfApprovalReasons: own, approvalReasons: foreign };
  }, [isVoucherAboveLimit, isVoucherAboveBudget, canApproveHighValueVoucher, canApproveBudget]);

  const needsForeignApproval = approvalReasons.length > 0;
  const needsSelfApproval = selfApprovalReasons.length > 0;

  const loadBudget = useCallback(
    async branchIdInput => {
      const branchId =
        typeof branchIdInput === 'object' && branchIdInput
          ? branchIdInput._id
          : branchIdInput;
      if (!branchId) {
        setBudget(null);
        return;
      }
      setBudgetLoading(true);
      try {
        const result = await getCourtesyVoucherBudget({ branchId });
        setBudget(result || null);
      } catch {
        // The bar is a hint, not a gate — the backend decides on send.
        setBudget(null);
      } finally {
        setBudgetLoading(false);
      }
    },
    [getCourtesyVoucherBudget]
  );

  useEffect(() => {
    if (!canRespond || !selected?.branchId) {
      setBudget(null);
      return;
    }
    loadBudget(selected.branchId);
  }, [canRespond, selected?._id, selected?.branchId, loadBudget]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCustomerFeedback({
        status: statusFilter || null,
        entertainmentId: entertainmentFilter || null,
        pendingApproval: approvalFilter,
      });
      if (result) {
        setItems(result.items || []);
        setTotal(result.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [getCustomerFeedback, statusFilter, entertainmentFilter, approvalFilter]);

  useEffect(() => {
    if (!canRead) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, entertainmentFilter, approvalFilter, canRead]);

  // Select the case a mail deep-linked to, once it has arrived with the list.
  useEffect(() => {
    if (!linkedFeedback.feedbackId) return;
    const match = items.find(item => item._id === linkedFeedback.feedbackId);
    if (!match) return;
    setSelected(current => current || match);
  }, [linkedFeedback.feedbackId, items]);

  // Re-evaluate deadline warning levels every minute.
  useEffect(() => {
    const interval = setInterval(() => setNowTs(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;

    const init = async () => {
      // Fetch the current user's assigned branches, all entertainments, entertainment types
      // and the active classification categories in parallel
      const [branchResult, allEntertainments, allEntertainmentTypes, categoriesResult] = await Promise.all([
        getMyBranches().catch(() => null),
        getEntertainments().catch(() => null),
        getEntertainmentTypes().catch(() => null),
        getCategories(true).catch(() => null),
      ]);

      const branchIds = branchResult?.branchIds || [];
      setAssignedBranchIds(branchIds);

      if (categoriesResult?.categories) {
        setCategories(categoriesResult.categories);
      }

      if (allEntertainments) {
        // When the user is scoped to specific branches, filter the dropdown to those branches only
        const filtered =
          branchIds.length > 0
            ? allEntertainments.filter(e => branchIds.includes(e.branch?.toString() || e.branch))
            : allEntertainments;
        setEntertainments(filtered);
      }

      if (allEntertainmentTypes) {
        setEntertainmentTypes(Array.isArray(allEntertainmentTypes) ? allEntertainmentTypes : []);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(item => {
    setSelected(item);
    setPhoneVisible(false);

    const entertainmentName =
      item.entertainmentId?.longName || item.entertainmentId?.entertainmentName || '';

    let initialResponse = '';
    if (item.response) {
      initialResponse = item.response;
    } else if (item.message) {
      // Has customer text: pre-fill from AI suggestion if available
      initialResponse = item.aiSuggestion?.responseText || '';
    } else {
      // No customer text: always pre-fill with standard text
      initialResponse = buildNoMessageStandardText(item.customerFirstName, entertainmentName);
    }

    setResponseDisplay(initialResponse);

    // Only pre-fill voucher value if there's a customer message and AI suggests one
    const suggestedAmount =
      item.message && item.aiSuggestion?.voucherRecommended && item.aiSuggestion?.voucherAmount
        ? String(item.aiSuggestion.voucherAmount)
        : '';
    setVoucherValue(item.courtesyVoucherValue ? String(item.courtesyVoucherValue) : suggestedAmount);
    setCustomVoucherValue('');
    setSelfApprovalConfirmed(false);
    if (isMobile) setShowDetail(true);
  }, [isMobile]);

  const handleStatusChange = useCallback(
    async (feedbackId, newStatus) => {
      await updateStatus({ feedbackId, status: newStatus });
      const update = { status: newStatus };
      setItems(prev => prev.map(i => (i._id === feedbackId ? { ...i, ...update } : i)));
      if (selected?._id === feedbackId) setSelected(prev => ({ ...prev, ...update }));
      toast.success('Status aktualisiert');
    },
    [updateStatus, selected]
  );

  const handleClassificationChange = useCallback(
    async (feedbackId, classification) => {
      setClassificationLoading(true);
      try {
        await setClassification({ feedbackId, classification });
        const update = { classification, classificationSource: 'manual' };
        setItems(prev => prev.map(i => (i._id === feedbackId ? { ...i, ...update } : i)));
        if (selected?._id === feedbackId) setSelected(prev => ({ ...prev, ...update }));
        toast.success('Klassifikation aktualisiert');
      } finally {
        setClassificationLoading(false);
      }
    },
    [setClassification, selected]
  );

  const handleCalledConfirm = useCallback(
    async (callNote) => {
      if (!selected) return;
      setCalledLoading(true);
      try {
        const result = await updateCustomerCalled({
          feedbackId: selected._id,
          customerCalled: true,
          callNote: callNote || null,
        });
        const update = {
          customerCalled: true,
          callNote: result?.callNote || callNote || selected.callNote,
          calledAt: result?.calledAt || new Date().toISOString(),
        };
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        setSelected(prev => ({ ...prev, ...update }));
        setCallNoteDialogOpen(false);
        toast.success('Anruf dokumentiert');
      } finally {
        setCalledLoading(false);
      }
    },
    [updateCustomerCalled, selected]
  );

  const handleUnmarkCalled = useCallback(async () => {
    if (!selected) return;
    setCalledLoading(true);
    try {
      await updateCustomerCalled({ feedbackId: selected._id, customerCalled: false });
      const update = { customerCalled: false, callNote: null, calledAt: null };
      setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
      setSelected(prev => ({ ...prev, ...update }));
    } finally {
      setCalledLoading(false);
    }
  }, [updateCustomerCalled, selected]);

  const applyAiSuggestion = useCallback(
    (suggestion) => {
      if (!suggestion) return;
      if (suggestion.responseText) {
        setResponseDisplay(suggestion.responseText);
        if (responseRef.current) responseRef.current.value = suggestion.responseText;
      }
      if (suggestion.voucherRecommended && suggestion.voucherAmount) {
        setVoucherValue(String(suggestion.voucherAmount));
      }
    },
    []
  );

  const handleGenerateAi = useCallback(
    async (feedbackId) => {
      setAiLoadingItemId(feedbackId);
      if (selected?._id === feedbackId) setAiLoading(true);
      try {
        const result = await generateAiSuggestion({ feedbackId });
        if (result?.aiSuggestion) {
          const s = result.aiSuggestion;
          const update = { aiSuggestion: s };
          setItems(prev => prev.map(i => (i._id === feedbackId ? { ...i, ...update } : i)));
          if (selected?._id === feedbackId) {
            setSelected(prev => ({ ...prev, ...update }));
            // Auto-apply only when the response field is still empty
            const currentText = responseRef.current?.value?.trim();
            if (!currentText) {
              applyAiSuggestion(s);
            }
          }
          toast.success('KI-Vorschlag generiert');
        }
      } finally {
        setAiLoadingItemId(null);
        setAiLoading(false);
      }
    },
    [generateAiSuggestion, selected, applyAiSuggestion]
  );

  // Resolves the EntertainmentType document matching the selected feedback's entertainment tags
  const selectedEntertainmentType = useMemo(() => {
    if (!selected || entertainmentTypes.length === 0) return null;
    const entertainmentName = (selected.entertainmentId?.entertainmentName || '').toLowerCase();
    return entertainmentTypes.find(et => et.name.toLowerCase() === entertainmentName) || null;
  }, [selected, entertainmentTypes]);

  const handleSaveAiPrompt = useCallback(async () => {
    if (!aiPromptTypeId) return;
    setAiPromptSaving(true);
    try {
      const newPrompt = aiPromptRef.current?.value ?? '';
      const updated = await saveAiPrompt({
        entertainmentTypeId: aiPromptTypeId,
        experienceFeedbackAiPrompt: newPrompt,
      });
      setEntertainmentTypes(prev =>
        prev.map(et => (et._id === updated._id ? updated : et))
      );
      toast.success('KI-Prompt gespeichert');
      setAiPromptDialogOpen(false);
    } catch {
      toast.error('Fehler beim Speichern des KI-Prompts');
    } finally {
      setAiPromptSaving(false);
    }
  }, [saveAiPrompt, aiPromptTypeId]);

  const handleSendResponse = useCallback(async () => {
    const responseText = responseRef.current?.value?.trim() || responseDisplay.trim();
    if (!responseText) {
      toast.error('Bitte einen Antworttext eingeben');
      return;
    }

    if (voucherValue === 'custom' && !isValidVoucherAmount(customVoucherValue)) {
      toast.error(
        `Bitte einen Gutscheinbetrag zwischen 1 und ${MAX_COURTESY_VOUCHER_AMOUNT} € eingeben`
      );
      return;
    }

    const resolvedVoucherValue =
      voucherValue === 'custom'
        ? Number(customVoucherValue) || null
        : voucherValue
        ? Number(voucherValue)
        : null;

    // What the user may wave through themselves still needs one deliberate click.
    if (needsSelfApproval && !selfApprovalConfirmed) {
      toast.error('Bitte erteile die Freigabe, bevor du die Antwort sendest.');
      return;
    }

    setSendLoading(true);
    try {
      const result = await sendResponse({
        feedbackId: selected._id,
        responseText,
        courtesyVoucherValue: resolvedVoucherValue,
      });

      // Above the limit or the monthly budget without the matching rights: the
      // backend parked the answer instead of sending it. It goes out on its own
      // once someone approves it — nothing has to be written again.
      if (result?.requiresApproval) {
        const update = {
          response: responseText,
          status: selected.status === 'new' ? 'inProgress' : selected.status,
          pendingApproval: { ...result.approvalRequest, canDecide: false },
          lastApproval: null,
        };
        setSelected(prev => ({ ...prev, ...update }));
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        toast.success(
          'Freigabe angefragt – der Status steht ab jetzt am Fall, die Antwort geht nach der Freigabe automatisch raus'
        );
        loadBudget(selected.branchId);
        return;
      }

      const updatedItem = {
        ...selected,
        response: responseText,
        responseEmailSentAt: new Date().toISOString(),
        status: 'resolved',
        courtesyVoucherCode: result?.courtesyVoucherCode || selected.courtesyVoucherCode,
        courtesyVoucherValue: result?.courtesyVoucherValue || resolvedVoucherValue,
      };
      setSelected(updatedItem);
      setItems(prev => prev.map(i => (i._id === selected._id ? updatedItem : i)));
      toast.success('Antwort gesendet und Feedback als gelöst markiert');
      loadBudget(selected.branchId);
    } catch (err) {
      if (err?.code === 'FEEDBACK_APPROVAL_PENDING') {
        toast.error('Für dieses Feedback wartet bereits eine Antwort auf Freigabe.');
      } else {
        throw err;
      }
    } finally {
      setSendLoading(false);
    }
  }, [
    sendResponse,
    selected,
    responseDisplay,
    voucherValue,
    customVoucherValue,
    needsSelfApproval,
    selfApprovalConfirmed,
    loadBudget,
  ]);

  // Approving dispatches the parked answer right away: the voucher is issued and
  // the mail the employee wrote goes out unchanged.
  const handleApproveVoucherRequest = useCallback(async () => {
    if (!pendingApproval) return;
    setApprovalLoading(true);
    try {
      const result = await approveVoucherRequest({ requestId: pendingApproval._id });
      const update = {
        response: pendingApproval.responseText,
        responseEmailSentAt: new Date().toISOString(),
        status: 'resolved',
        courtesyVoucherCode: result?.courtesyVoucherCode || null,
        courtesyVoucherValue: result?.courtesyVoucherValue || pendingApproval.amount,
        pendingApproval: null,
        lastApproval: result?.approvalRequest || null,
      };
      setSelected(prev => ({ ...prev, ...update }));
      setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
      toast.success('Freigegeben – Gutschein erstellt und Antwort an den Kunden gesendet');
      loadBudget(selected?.branchId);
    } catch (err) {
      if (err?.code === 'RESPONSE_ALREADY_SENT') {
        toast.error('Dieses Feedback wurde zwischenzeitlich schon beantwortet.');
      }
      // other cases already surfaced a toast in the hook
    } finally {
      setApprovalLoading(false);
    }
  }, [approveVoucherRequest, pendingApproval, selected, loadBudget]);

  const handleRejectVoucherRequest = useCallback(
    async comment => {
      if (!pendingApproval) return;
      setApprovalLoading(true);
      try {
        const result = await rejectVoucherRequest({
          requestId: pendingApproval._id,
          comment: comment || null,
        });
        const update = {
          pendingApproval: null,
          lastApproval: result?.approvalRequest || null,
        };
        setSelected(prev => ({ ...prev, ...update }));
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        setRejectApprovalDialogOpen(false);
        toast.success('Freigabe abgelehnt – das Feedback bleibt offen');
        loadBudget(selected?.branchId);
      } finally {
        setApprovalLoading(false);
      }
    },
    [rejectVoucherRequest, pendingApproval, selected, loadBudget]
  );

  // Accept the AI's auto-resolve assessment: the backend sends the AI response +
  // voucher and resolves the feedback. Confirms the AI was right.
  const handleAcceptTriage = useCallback(async () => {
    if (!selected) return;
    setTriageLoading(true);
    try {
      const result = await acceptAiTriage({ feedbackId: selected._id });

      // The AI's recommended voucher needs a decision first — the confirmed
      // answer waits and is sent automatically once it is approved.
      if (result?.requiresApproval) {
        const update = {
          response: selected.aiSuggestion?.responseText || selected.response,
          status: selected.status === 'new' ? 'inProgress' : selected.status,
          aiTriage: result?.aiTriage || { decision: 'accepted' },
          pendingApproval: { ...result.approvalRequest, canDecide: false },
          lastApproval: null,
        };
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        setSelected(prev => ({ ...prev, ...update }));
        toast.success(
          'Freigabe angefragt – die KI-Antwort wird nach der Freigabe automatisch gesendet'
        );
        loadBudget(selected.branchId);
        return;
      }

      const update = {
        status: 'resolved',
        responseEmailSentAt: new Date().toISOString(),
        response: selected.aiSuggestion?.responseText || selected.response,
        courtesyVoucherCode: result?.courtesyVoucherCode || selected.courtesyVoucherCode,
        courtesyVoucherValue: result?.courtesyVoucherValue || selected.courtesyVoucherValue,
        aiTriage: result?.aiTriage || { decision: 'accepted' },
      };
      setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
      setSelected(prev => ({ ...prev, ...update }));
      toast.success('KI-Antwort bestätigt und an den Kunden gesendet');
      loadBudget(selected.branchId);
    } catch (err) {
      if (err?.code === 'FEEDBACK_APPROVAL_PENDING') {
        toast.error('Für dieses Feedback wartet bereits eine Antwort auf Freigabe.');
      }
      // hook already surfaced a generic error toast for other cases
    } finally {
      setTriageLoading(false);
    }
  }, [acceptAiTriage, selected, loadBudget]);

  // Reject the AI's auto-resolve assessment: the feedback stays in the manual
  // queue and the rejection is logged for admin review.
  const handleRejectTriageConfirm = useCallback(
    async (rejectionNote) => {
      if (!selected) return;
      setTriageLoading(true);
      try {
        const result = await rejectAiTriage({ feedbackId: selected._id, rejectionNote: rejectionNote || null });
        const update = { aiTriage: result?.aiTriage || { decision: 'rejected', rejectionNote } };
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        setSelected(prev => ({ ...prev, ...update }));
        setRejectDialogOpen(false);
        toast.success('KI-Einschätzung abgelehnt – Fall bleibt zur manuellen Bearbeitung');
      } finally {
        setTriageLoading(false);
      }
    },
    [rejectAiTriage, selected]
  );

  const openReviewDialog = useCallback(async () => {
    setReviewDialogOpen(true);
    setRejectedLoading(true);
    try {
      const result = await getRejectedAiTriage({ limit: 100 });
      setRejectedItems(result?.items || []);
    } finally {
      setRejectedLoading(false);
    }
  }, [getRejectedAiTriage]);

  const handleResizeStart = e => {
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing || !containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - resizeStartX.current;
      const deltaPercentage = (deltaX / containerWidth) * 100;
      const minWidthPercentage = Math.max(25, (280 / containerWidth) * 100);
      const newWidth = Math.max(
        minWidthPercentage,
        Math.min(80, resizeStartWidth.current + deltaPercentage)
      );

      setLeftPanelWidth(newWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  const renderListItem = (item) => {
    const { label: statusLabel, color: statusColor } = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
    const isSelected = selected?._id === item._id;
    const isAiLoading = aiLoadingItemId === item._id;
    const deadline = getFeedbackDeadline(item, nowTs);
    const stage = getEscalationStage(item, nowTs);
    // The AI flagged this case as auto-resolvable and no human has confirmed yet.
    const aiAutoPending =
      item.aiSuggestion?.autoResolvable && (item.aiTriage?.decision ?? 'pending') === 'pending';
    const deadlineBorderColor =
      deadline.level === 'overdue' || deadline.level === 'red'
        ? 'error.main'
        : deadline.level === 'orange'
        ? 'warning.main'
        : deadline.level === 'green'
        ? 'success.main'
        : 'transparent';

    return (
      <Box
        key={item._id}
        onClick={() => handleSelect(item)}
        sx={{
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderLeft: '4px solid',
          borderLeftColor: deadlineBorderColor,
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <ThumbDownAltIcon fontSize='small' sx={{ color: 'error.main', flexShrink: 0 }} />
            <Typography variant='subtitle2' noWrap sx={{ minWidth: 0 }}>
              {item.customerFirstName || 'Kunde'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexShrink: 1,
              flexWrap: 'wrap',
              minWidth: 0,
              maxWidth: '55%',
            }}
          >
            {aiAutoPending && (
              <Tooltip title='KI hält diesen Fall für automatisch beantwortbar'>
                <SmartToyIcon fontSize='small' sx={{ color: 'primary.main' }} />
              </Tooltip>
            )}
            {item.customerCalled && (
              <Tooltip title='Angerufen'>
                <PhoneInTalkIcon fontSize='small' sx={{ color: 'success.main' }} />
              </Tooltip>
            )}
            {item.pendingApproval && (
              <Tooltip
                title={
                  item.pendingApproval.canDecide
                    ? `Antwort mit ${formatEuro(item.pendingApproval.amount)} € Kulanzgutschein wartet auf deine Freigabe`
                    : `Antwort mit ${formatEuro(item.pendingApproval.amount)} € Kulanzgutschein wartet auf Freigabe durch ${describeApprovalDeciders(item.pendingApproval.reasons)}`
                }
              >
                <Chip
                  icon={<HourglassEmptyIcon sx={{ fontSize: '0.7rem' }} />}
                  label={item.pendingApproval.canDecide ? 'Freigabe: du' : 'Freigabe offen'}
                  color='info'
                  size='small'
                  variant={item.pendingApproval.canDecide ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.6rem' }}
                />
              </Tooltip>
            )}
            {deadline.level !== 'none' && (
              <Tooltip title={`Beschwerde ${formatFeedbackAge(deadline.ageMs)} eingegangen`}>
                <Chip
                  label={deadline.label}
                  color={deadline.color}
                  size='small'
                  variant={deadline.level === 'overdue' ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.6rem' }}
                />
              </Tooltip>
            )}
            <Chip
              label={statusLabel}
              color={statusColor}
              size='small'
              sx={{
                height: 20,
                fontSize: '0.65rem',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant='caption' color='text.secondary' noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
            {item.entertainmentId?.longName || item.entertainmentId?.entertainmentName || '—'}
          </Typography>
          {stage.stage !== 'none' && (
            <Tooltip title={`Aktuelle Eskalationsstufe: ${stage.label}`}>
              <Chip
                label={`Stufe: ${stage.label}`}
                color={stage.color}
                size='small'
                variant='outlined'
                sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Box>

        {item.message ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 0.5, fontSize: '0.75rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {item.message}
          </Typography>
        ) : (
          <Typography variant='caption' color='text.disabled' sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            Kein schriftliches Feedback
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
          <Typography variant='caption' color='text.secondary'>
            {formatDate(item.meta?.createdAt)}
          </Typography>
          {canRespond && item.message && (
            <Tooltip title={item.aiSuggestion ? 'KI-Vorschlag neu generieren' : 'KI-Vorschlag generieren'}>
              <IconButton
                size='small'
                onClick={e => { e.stopPropagation(); handleGenerateAi(item._id); }}
                disabled={isAiLoading}
                sx={{ p: 0.25 }}
              >
                {isAiLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesomeIcon fontSize='small' sx={{ color: item.aiSuggestion ? 'success.main' : 'action.active' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderList = () => (
    <Box
      sx={{
        flex: { xs: '1 1 100%', md: `0 0 ${leftPanelWidth}%` },
        width: { xs: '100%', md: `${leftPanelWidth}%` },
        minWidth: { xs: '100%', md: 280 },
        overflow: 'auto',
        display: isMobile && showDetail ? 'none' : 'flex',
        flexDirection: 'column',
        transition: isResizing ? 'none' : 'width 0.3s ease, flex-basis 0.3s ease',
      }}
    >
      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => { if (v !== null) setStatusFilter(v); }}
          size='small'
          fullWidth
          sx={{
            mb: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            '& .MuiToggleButtonGroup-grouped': {
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              flex: '1 1 90px',
              minWidth: 0,
              px: 0.75,
            },
          }}
        >
          <ToggleButton value='new' sx={{ fontSize: '0.72rem' }}>Neu</ToggleButton>
          <ToggleButton value='inProgress' sx={{ fontSize: '0.72rem' }}>Bearbeitung</ToggleButton>
          <ToggleButton value='resolved' sx={{ fontSize: '0.72rem' }}>Gelöst</ToggleButton>
        </ToggleButtonGroup>

        <FormControl fullWidth size='small'>
          <InputLabel id='ent-filter-label'>Entertainment</InputLabel>
          <Select
            labelId='ent-filter-label'
            value={entertainmentFilter}
            label='Entertainment'
            onChange={e => setEntertainmentFilter(e.target.value)}
          >
            <MenuItem value=''>Alle</MenuItem>
            {entertainments.map(e => (
              <MenuItem key={e._id} value={e._id}>
                {e.longName || e.entertainmentName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Inbox of answers waiting for a voucher decision — own requests and,
            for approvers, everything they may decide. */}
        <Chip
          icon={<HourglassEmptyIcon />}
          label='Nur Gutschein-Freigaben'
          size='small'
          color={approvalFilter ? 'info' : 'default'}
          variant={approvalFilter ? 'filled' : 'outlined'}
          onClick={() => setApprovalFilter(v => !v)}
          sx={{ mt: 1.5, cursor: 'pointer' }}
        />
      </Box>

      {/* List header */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='caption' color='text.secondary'>
          {total} negatives Feedback
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {canReviewAiTriage && (
            <Tooltip title='Abgelehnte KI-Einschätzungen prüfen'>
              <IconButton size='small' onClick={openReviewDialog}>
                <FactCheckIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size='small' onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize='small' />}
          </IconButton>
        </Box>
      </Box>

      {items.length === 0 && !loading && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>Keine Einträge</Typography>
        </Box>
      )}

      {items.map(renderListItem)}
    </Box>
  );

  const renderDetail = () => {
    if (!selected) {
      if (isMobile && showDetail) return null;
      return (
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Wähle einen Eintrag aus der Liste aus
          </Typography>
        </Box>
      );
    }

    const { label: statusLabel, color: statusColor } = STATUS_CONFIG[selected.status] || STATUS_CONFIG.new;
    const alreadySent = Boolean(selected.responseEmailSentAt);
    const resolvedVoucherValueForDisplay =
      voucherValue === 'custom' ? customVoucherValue : voucherValue;
    const deadline = getFeedbackDeadline(selected, nowTs);
    const stage = getEscalationStage(selected, nowTs);
    const triageDecision = selected.aiTriage?.decision ?? 'pending';
    // Show the accept/reject banner only while the AI flagged auto-resolution,
    // nothing has been sent, and no decision has been recorded yet. An answer
    // already parked for approval blocks any further send (the backend answers
    // FEEDBACK_APPROVAL_PENDING), so offering "Annehmen & senden" next to the
    // waiting panel would only produce an error — and two contradicting amounts.
    const showTriageBanner =
      canRespond &&
      selected.message &&
      selected.aiSuggestion?.autoResolvable &&
      !alreadySent &&
      !pendingApproval &&
      triageDecision === 'pending';

    return (
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: isMobile && !showDetail ? 'none' : 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mobile back button */}
        {isMobile && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Button size='small' onClick={() => setShowDetail(false)}>
              ← Zurück zur Liste
            </Button>
          </Box>
        )}

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant='h6'>
                {selected.customerFirstName || 'Kunde'}{' '}
                <Typography component='span' variant='body2' color='text.secondary'>
                  ({selected.customerEmail})
                </Typography>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {selected.entertainmentId?.longName || '—'} · {formatDate(selected.meta?.createdAt)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                {stage.stage !== 'none' && (
                  <Tooltip title={`Aktuelle Eskalationsstufe: ${stage.label}`}>
                    <Chip
                      label={`Stufe: ${stage.label}`}
                      color={stage.color}
                      size='small'
                      sx={{ height: 22, fontSize: '0.68rem' }}
                    />
                  </Tooltip>
                )}
                {deadline.level !== 'none' && (
                  <Chip
                    label={
                      deadline.level === 'overdue'
                        ? `Überfällig · ${formatFeedbackAge(deadline.ageMs)} offen`
                        : `${deadline.label} · ${formatFeedbackAge(deadline.ageMs)}`
                    }
                    color={deadline.color}
                    size='small'
                    variant={deadline.level === 'overdue' ? 'filled' : 'outlined'}
                    sx={{ height: 22, fontSize: '0.68rem' }}
                  />
                )}
              </Box>
              {selected.bookingNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Buchung #{selected.bookingNumber}
                  </Typography>
                  {selected.bookingId && (
                    <Tooltip title='Buchung öffnen'>
                      <IconButton
                        size='small'
                        sx={{ p: 0.25 }}
                        onClick={() => setBookingModalId(
                          typeof selected.bookingId === 'object' ? selected.bookingId._id : selected.bookingId
                        )}
                      >
                        <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <FormControl size='small' sx={{ minWidth: 160 }}>
              <InputLabel id='status-change-label'>Status</InputLabel>
              <Select
                labelId='status-change-label'
                value={selected.status}
                label='Status'
                onChange={e => handleStatusChange(selected._id, e.target.value)}
                disabled={!canRespond}
              >
                <MenuItem value='new'>Neu</MenuItem>
                <MenuItem value='inProgress'>In Bearbeitung</MenuItem>
                <MenuItem value='resolved'>Gelöst</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            {/* Single column: all content stacks vertically */}
            <Grid item xs={12}>

              {/* Booking meta + classification + responsible employees */}
              <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Personenanzahl
                    </Typography>
                    <Typography variant='body1'>
                      {selected.bookingParticipantCount != null ? selected.bookingParticipantCount : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Buchungswert
                    </Typography>
                    <Typography variant='body1'>
                      {selected.bookingValue != null ? currencyFormatter.format(selected.bookingValue) : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Besuchsdatum
                    </Typography>
                    <Typography variant='body1'>
                      {selected.bookingDate ? formatDate(selected.bookingDate) : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                      Klassifikation (automatisch durch KI – bei Bedarf korrigieren)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {canRespond ? (
                        <FormControl size='small' sx={{ minWidth: 200 }}>
                          <Select
                            value={selected.classification || ''}
                            displayEmpty
                            disabled={classificationLoading}
                            onChange={e => handleClassificationChange(selected._id, e.target.value)}
                          >
                            <MenuItem value=''>
                              <em>Unklassifiziert</em>
                            </MenuItem>
                            {selected.classification &&
                              !categories.some(c => c.name === selected.classification) && (
                                <MenuItem value={selected.classification}>{selected.classification}</MenuItem>
                              )}
                            {categories.map(c => (
                              <MenuItem key={c._id || c.name} value={c.name}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={selected.classification || 'Unklassifiziert'}
                          size='small'
                          color={selected.classification ? 'primary' : 'default'}
                          variant='outlined'
                        />
                      )}
                      {selected.classificationSource && (
                        <Chip
                          label={`Quelle: ${CLASSIFICATION_SOURCE_LABELS[selected.classificationSource] || selected.classificationSource}`}
                          size='small'
                          variant='outlined'
                        />
                      )}
                      {classificationLoading && <CircularProgress size={16} />}
                    </Box>
                  </Grid>

                  {Array.isArray(selected.responsibleEmployees) && selected.responsibleEmployees.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                        Verantwortliche Mitarbeiter vor Ort
                      </Typography>
                      <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }} useFlexGap>
                        {selected.responsibleEmployees.map((emp, idx) => (
                          <Tooltip
                            key={emp.userId || emp.username || idx}
                            title={Array.isArray(emp.actions) && emp.actions.length > 0 ? emp.actions.join(', ') : ''}
                          >
                            <Chip
                              label={
                                Array.isArray(emp.actions) && emp.actions.length > 0
                                  ? `${emp.username || 'Mitarbeiter'} · ${emp.actions.join(', ')}`
                                  : emp.username || 'Mitarbeiter'
                              }
                              size='small'
                              variant='outlined'
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Customer message */}
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Kundennachricht</Typography>
              {selected.message ? (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'background.default',
                    borderColor: 'error.light',
                  }}
                >
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    Eingegangen: {formatDate(selected.messageSubmittedAt)}
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.message}
                  </Typography>
                </Paper>
              ) : (
                <Alert severity='info' sx={{ mb: 2 }}>
                  Kein schriftliches Feedback vorhanden
                </Alert>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* AI section – generate when no suggestion yet; regenerate button when already generated */}
              {canRespond && selected.message && !alreadySent && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    variant={selected.aiSuggestion ? 'outlined' : 'contained'}
                    size='small'
                    onClick={() => handleGenerateAi(selected._id)}
                    disabled={aiLoading}
                    sx={{ flex: '1 1 auto' }}
                  >
                    {selected.aiSuggestion ? 'KI-Vorschlag neu generieren' : 'KI-Vorschlag generieren'}
                  </Button>
                  {canEditAiPrompt && (
                    <Tooltip title='KI-Prompt bearbeiten'>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setAiPromptTypeId(selectedEntertainmentType?._id || '');
                          setAiPromptDialogOpen(true);
                        }}
                      >
                        Prompt
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* AI suggestion result card */}
              {canRespond && selected.message && selected.aiSuggestion && (
                <Box sx={{ mb: 2 }}>
                  <Paper
                    variant='outlined'
                    sx={{ p: 2, mb: 1.5, bgcolor: 'primary.50', borderColor: 'primary.light' }}
                  >
                    <Typography
                      variant='caption'
                      color='primary.main'
                      sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                    >
                      KI-Vorschlag ({formatDate(selected.aiSuggestion.generatedAt)})
                    </Typography>

                    {selected.message && selected.message.length > 200 && selected.aiSuggestion.summary && (
                      <Alert
                        severity='info'
                        sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                      >
                        <strong>Zusammenfassung:</strong> {selected.aiSuggestion.summary}
                      </Alert>
                    )}

                    {selected.aiSuggestion.callRecommended && (
                      <Chip
                        icon={<CallIcon fontSize='small' />}
                        label='Telefonat empfohlen'
                        color='warning'
                        size='small'
                        sx={{
                          mb: 1.5,
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    )}

                    {selected.aiSuggestion.voucherRecommended && (
                      <Alert
                        severity='success'
                        sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                      >
                        <strong>Gutschein empfohlen:</strong> {selected.aiSuggestion.voucherAmount} € —{' '}
                        {selected.aiSuggestion.voucherReason}
                      </Alert>
                    )}

                    {selected.aiSuggestion.responseText && (
                      <Box>
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                          Vorgeschlagener Antworttext:
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            borderRadius: 1,
                            p: 1,
                            bgcolor: 'action.hover',
                            mb: 1,
                          }}
                        >
                          {selected.aiSuggestion.responseText}
                        </Typography>
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => applyAiSuggestion(selected.aiSuggestion)}
                        >
                          Vorschlag übernehmen
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* AI auto-resolve triage banner: the AI thinks this case can be
                  answered automatically. In this first phase a human confirms
                  (send now) or rejects (keep for manual handling). */}
              {showTriageBanner && (
                <Paper
                  variant='outlined'
                  sx={{ p: 2, mb: 2, borderColor: 'primary.main', bgcolor: 'primary.50' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SmartToyIcon color='primary' />
                    <Typography variant='subtitle2' color='primary.main'>
                      KI hält diesen Fall für sofort beantwortbar
                    </Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                    Die KI meint, ihr Antwortvorschlag
                    {selected.aiSuggestion?.voucherRecommended && selected.aiSuggestion?.voucherAmount
                      ? ` inkl. Gutschein über ${selected.aiSuggestion.voucherAmount} €`
                      : ''}{' '}
                    könnte ohne weitere Prüfung direkt an den Kunden gesendet werden.
                    {selected.aiSuggestion?.autoResolveReason
                      ? ` Begründung: ${selected.aiSuggestion.autoResolveReason}`
                      : ''}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
                    Prüfe, ob die Einschätzung stimmt: <strong>Annehmen</strong> sendet die KI-Antwort
                    sofort. <strong>Ablehnen</strong> behält den Fall zur manuellen Bearbeitung.
                  </Typography>
                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    <Button
                      variant='contained'
                      color='success'
                      startIcon={triageLoading ? <CircularProgress size={16} color='inherit' /> : <CheckIcon />}
                      onClick={handleAcceptTriage}
                      disabled={triageLoading}
                    >
                      Annehmen &amp; senden
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      startIcon={<CloseIcon />}
                      onClick={() => setRejectDialogOpen(true)}
                      disabled={triageLoading}
                    >
                      Ablehnen
                    </Button>
                  </Stack>
                </Paper>
              )}

              {/* Recorded rejection of the AI's auto-resolve assessment */}
              {canRespond && selected.aiSuggestion?.autoResolvable && triageDecision === 'rejected' && !alreadySent && (
                <Alert severity='info' icon={<SmartToyIcon />} sx={{ mb: 2 }}>
                  KI-Sofortbeantwortung abgelehnt – bitte manuell bearbeiten.
                  {selected.aiTriage?.rejectionNote ? ` (${selected.aiTriage.rejectionNote})` : ''}
                </Alert>
              )}

              {/* Response composer */}
              {canRespond && (
                <Box>
                  <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
                    Antwort an den Kunden
                  </Typography>

                  {alreadySent ? (
                    <Alert severity='success' icon={<MarkEmailReadIcon />} sx={{ mb: 2 }}>
                      Antwort am {formatDate(selected.responseEmailSentAt)} gesendet.
                    </Alert>
                  ) : null}

                  {/* Answer parked for approval: it is kept complete and goes out
                      by itself as soon as someone approves the voucher. */}
                  {pendingApproval && (
                    <Paper
                      variant='outlined'
                      sx={{ p: 2, mb: 2, borderColor: 'info.main', borderLeftWidth: 4 }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                        <HourglassEmptyIcon fontSize='small' color='info' />
                        <Typography variant='subtitle2'>Freigabe-Status</Typography>
                        <Chip
                          label='Wartet auf Freigabe'
                          color='info'
                          size='small'
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Stack>

                      <ApprovalStatusTimeline request={pendingApproval} formatDate={formatDate} />

                      <Paper variant='outlined' sx={{ p: 1.5, mt: 1.5, bgcolor: 'action.hover' }}>
                        <Typography variant='caption' color='text.secondary' display='block'>
                          Antwort, die nach der Freigabe gesendet wird
                        </Typography>
                        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                          {pendingApproval.responseText}
                        </Typography>
                      </Paper>

                      <Stack direction='row' spacing={1} sx={{ mt: 1.5 }} flexWrap='wrap' useFlexGap>
                        {pendingApproval.canDecide && (
                          <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            startIcon={
                              approvalLoading ? (
                                <CircularProgress size={14} color='inherit' />
                              ) : (
                                <CheckIcon />
                              )
                            }
                            onClick={handleApproveVoucherRequest}
                            disabled={approvalLoading}
                          >
                            Freigeben &amp; senden
                          </Button>
                        )}
                        {(pendingApproval.canDecide || isOwnApprovalRequest) && (
                          <Button
                            size='small'
                            variant='outlined'
                            color={pendingApproval.canDecide ? 'error' : 'inherit'}
                            startIcon={<CloseIcon />}
                            onClick={() => setRejectApprovalDialogOpen(true)}
                            disabled={approvalLoading}
                          >
                            {pendingApproval.canDecide ? 'Ablehnen' : 'Anfrage zurückziehen'}
                          </Button>
                        )}
                        {!pendingApproval.canDecide && !isOwnApprovalRequest && (
                          <Typography variant='caption' color='text.secondary'>
                            Du kannst diese Anfrage nicht entscheiden.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* A decided request keeps its outcome visible: a rejection hands
                      the case back with its reason, an approval documents what was
                      sent and by whom. */}
                  {!pendingApproval && lastApproval && lastApproval.status !== 'pending' && (
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeftWidth: 4,
                        borderColor:
                          lastApproval.status === 'approved' ? 'success.main' : 'warning.main',
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                        {lastApproval.status === 'approved' ? (
                          <CheckCircleIcon fontSize='small' color='success' />
                        ) : (
                          <ErrorOutlineIcon fontSize='small' color='warning' />
                        )}
                        <Typography variant='subtitle2'>Freigabe-Status</Typography>
                        <Chip
                          label={
                            lastApproval.status === 'approved'
                              ? 'Freigegeben'
                              : lastApproval.status === 'rejected'
                              ? 'Abgelehnt'
                              : 'Hinfällig'
                          }
                          color={lastApproval.status === 'approved' ? 'success' : 'warning'}
                          size='small'
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Stack>

                      <ApprovalStatusTimeline request={lastApproval} formatDate={formatDate} />
                    </Paper>
                  )}

                  {/* Response area: text field + fixed sign-off in one outlined container */}
                  {!pendingApproval && (
                  <Paper variant='outlined' sx={{ mb: 2, overflow: 'hidden' }}>
                    <TextField
                      key={selected._id}
                      inputRef={responseRef}
                      multiline
                      minRows={5}
                      fullWidth
                      variant='standard'
                      defaultValue={responseDisplay}
                      disabled={alreadySent}
                      placeholder='Schreibe hier die Antwort an den Kunden...'
                      sx={{
                        px: 1.75,
                        pt: 1.5,
                        pb: 1,
                        '& .MuiInput-underline:before': { display: 'none' },
                        '& .MuiInput-underline:after': { display: 'none' },
                      }}
                    />
                    <Divider />
                    <Box sx={{ px: 1.75, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant='caption' color='text.disabled' sx={{ display: 'block', fontStyle: 'italic' }}>
                        Viele Grüße
                      </Typography>
                      <Typography variant='caption' color='text.disabled' sx={{ fontStyle: 'italic' }}>
                        Dein {selected.entertainmentId?.longName || selected.entertainmentId?.entertainmentName || 'FunZone'} Team
                      </Typography>
                    </Box>
                  </Paper>
                  )}

                  {/* Voucher value selection – only offered when the customer left a message */}
                  {!alreadySent && !pendingApproval && selected.message && (() => {
                    const effectiveLimit = effectiveVoucherLimit;
                    const resolvedNum = resolvedVoucherNum;

                    // Bar fill: what the month already committed (issued vouchers
                    // plus answers waiting for approval), and on top of it what
                    // this answer would add.
                    const budgetTotal = Number(budget?.budget || 0);
                    const committed = Math.max(0, budgetTotal - budgetRemaining);
                    const committedPercent =
                      budgetTotal > 0 ? Math.min(100, (committed / budgetTotal) * 100) : 0;
                    const requestedPercent =
                      budgetTotal > 0
                        ? Math.min(100 - committedPercent, (resolvedNum / budgetTotal) * 100)
                        : 0;
                    const remainingAfter = budgetRemaining - resolvedNum;

                    return (
                      <Box sx={{ mb: 2 }}>
                        {/* Monthly goodwill budget of this branch — visible before
                            an amount is picked, not only when a send fails. */}
                        {(budget || budgetLoading) && (
                          <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
                            {budgetLoading || !budget ? (
                              <Stack direction='row' spacing={1} alignItems='center'>
                                <CircularProgress size={14} />
                                <Typography variant='caption' color='text.secondary'>
                                  Kulanz-Budget wird geladen …
                                </Typography>
                              </Stack>
                            ) : (
                              <>
                                <Stack
                                  direction='row'
                                  alignItems='baseline'
                                  justifyContent='space-between'
                                  spacing={1}
                                  sx={{ mb: 0.75 }}
                                >
                                  <Typography variant='caption' color='text.secondary'>
                                    Kulanz-Budget {MONTH_NAMES[(budget.month || 1) - 1]}{' '}
                                    {budget.year}
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    sx={{ fontWeight: 600 }}
                                    color={budgetRemaining <= 0 ? 'error.main' : 'text.primary'}
                                  >
                                    {formatEuro(budgetRemaining)} € von {formatEuro(budgetTotal)} €
                                    übrig
                                  </Typography>
                                </Stack>

                                <Box sx={{ position: 'relative' }}>
                                  <LinearProgress
                                    variant='determinate'
                                    value={committedPercent}
                                    color={budgetRemaining <= 0 ? 'error' : 'primary'}
                                    sx={{ height: 8, borderRadius: 1 }}
                                  />
                                  {/* What the currently picked amount would consume */}
                                  {requestedPercent > 0 && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        left: `${committedPercent}%`,
                                        width: `${requestedPercent}%`,
                                        borderRadius: 1,
                                        bgcolor: theme =>
                                          isVoucherAboveBudget
                                            ? theme.palette.error.light
                                            : theme.palette.warning.main,
                                      }}
                                    />
                                  )}
                                </Box>

                                <Stack
                                  direction='row'
                                  justifyContent='space-between'
                                  spacing={1}
                                  sx={{ mt: 0.75, flexWrap: 'wrap' }}
                                  useFlexGap
                                >
                                  <Typography variant='caption' color='text.disabled'>
                                    {formatEuro(budget.used)} € ausgegeben
                                    {Number(budget.pending || 0) > 0
                                      ? ` · ${formatEuro(budget.pending)} € für offene Freigaben reserviert`
                                      : ''}
                                  </Typography>
                                  {resolvedNum > 0 && (
                                    <Typography
                                      variant='caption'
                                      color={isVoucherAboveBudget ? 'error.main' : 'text.secondary'}
                                    >
                                      {isVoucherAboveBudget
                                        ? `${formatEuro(resolvedNum)} € übersteigen das Budget`
                                        : `Danach noch ${formatEuro(remainingAfter)} € übrig`}
                                    </Typography>
                                  )}
                                </Stack>
                              </>
                            )}
                          </Paper>
                        )}

                        <Stack
                          direction='row'
                          alignItems='center'
                          spacing={1}
                          useFlexGap
                          sx={{ mb: 1, flexWrap: 'wrap' }}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            Kulanzgutschein (wird beim Senden automatisch erstellt und der Mail angehängt)
                          </Typography>
                          <Typography variant='caption' color='text.disabled'>
                            · Limit ohne Freigabe: <strong>{effectiveLimit} €</strong>
                          </Typography>
                        </Stack>
                        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                          <Chip
                            label='Kein Gutschein'
                            variant={!voucherValue ? 'filled' : 'outlined'}
                            color='default'
                            onClick={() => { setVoucherValue(''); setCustomVoucherValue(''); setSelfApprovalConfirmed(false); }}
                            sx={{ cursor: 'pointer', maxWidth: '100%' }}
                          />
                          {VOUCHER_PRESETS.map(v => {
                            // Amounts above the limit or the remaining budget stay
                            // selectable — they route into the approval flow instead
                            // of being a dead end.
                            const aboveForPreset =
                              v > effectiveLimit || (!!budget && v > budgetRemaining);
                            const isSelected = voucherValue === String(v);
                            const needsApprovalForPreset =
                              (v > effectiveLimit && !canApproveHighValueVoucher) ||
                              (!!budget && v > budgetRemaining && !canApproveBudget);
                            return (
                              <Tooltip
                                key={v}
                                title={
                                  needsApprovalForPreset
                                    ? 'Freigabe erforderlich – die Antwort wird nach der Freigabe automatisch gesendet'
                                    : aboveForPreset
                                    ? 'Über Limit oder Budget – du kannst das selbst freigeben'
                                    : ''
                                }
                              >
                                <span>
                                  <Chip
                                    label={`${v} €`}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    color={isSelected ? (aboveForPreset ? 'warning' : 'primary') : 'default'}
                                    onClick={() => { setVoucherValue(String(v)); setCustomVoucherValue(''); setSelfApprovalConfirmed(false); }}
                                    sx={{ cursor: 'pointer', maxWidth: '100%' }}
                                  />
                                </span>
                              </Tooltip>
                            );
                          })}
                          <Chip
                            label='Benutzerdefiniert'
                            variant={voucherValue === 'custom' ? 'filled' : 'outlined'}
                            color={voucherValue === 'custom' ? 'secondary' : 'default'}
                            onClick={() => { setVoucherValue('custom'); setSelfApprovalConfirmed(false); }}
                            sx={{
                              cursor: 'pointer',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </Stack>

                        {voucherValue === 'custom' && (
                          <TextField
                            label='Betrag (€)'
                            type='number'
                            size='small'
                            value={customVoucherValue}
                            // Focusing selects the value so typing replaces it instead of
                            // appending to it.
                            onFocus={e => e.target.select()}
                            onChange={e => {
                              // The raw entry is kept: clamping every keystroke to [1, 100]
                              // appended the typed digit to the value already in the field and
                              // pushed the amount straight to the 100 € ceiling. The limits are
                              // checked below and before sending instead.
                              setCustomVoucherValue(e.target.value);
                              setSelfApprovalConfirmed(false);
                            }}
                            sx={{ mt: 1, width: 140 }}
                            inputProps={{ min: 1, max: MAX_COURTESY_VOUCHER_AMOUNT }}
                            error={!!customVoucherError}
                            helperText={customVoucherError || `Ohne Freigabe bis ${effectiveLimit} €`}
                          />
                        )}

                        {/* The user may wave this through themselves — one
                            deliberate click before the answer goes out. */}
                        {needsSelfApproval && (
                          <Alert
                            severity='warning'
                            sx={{
                              mt: 1.5,
                              alignItems: 'flex-start',
                              '& .MuiAlert-message': { minWidth: 0 },
                              '& .MuiAlert-action': {
                                pl: { xs: 0, sm: 2 },
                                ml: { xs: 0, sm: 'auto' },
                                mt: { xs: 1, sm: 0 },
                              },
                            }}
                            action={
                              <Button
                                size='small'
                                color='inherit'
                                variant={selfApprovalConfirmed ? 'contained' : 'outlined'}
                                onClick={() => setSelfApprovalConfirmed(v => !v)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {selfApprovalConfirmed ? 'Freigabe erteilt ✓' : 'Freigabe erteilen'}
                              </Button>
                            }
                          >
                            <strong>{formatEuro(resolvedNum)} €</strong> liegt{' '}
                            {describeApprovalReasons(selfApprovalReasons)}
                            {selfApprovalReasons.includes('limit') ? ` von ${effectiveLimit} €` : ''}
                            . Du darfst das selbst freigeben.
                          </Alert>
                        )}

                        {/* Someone else has to decide: the answer is parked and
                            sent automatically once approved. */}
                        {needsForeignApproval && (
                          <Alert severity='info' icon={<HourglassEmptyIcon />} sx={{ mt: 1.5 }}>
                            <strong>{formatEuro(resolvedNum)} €</strong> liegt{' '}
                            {describeApprovalReasons(approvalReasons)}
                            {approvalReasons.includes('limit') ? ` von ${effectiveLimit} €` : ''}
                            {approvalReasons.includes('budget')
                              ? ` (noch ${formatEuro(budgetRemaining)} € frei)`
                              : ''}
                            . Mit „Freigabe anfragen“ bleibt deine Antwort gespeichert und wird nach
                            der Freigabe automatisch an den Kunden gesendet.
                          </Alert>
                        )}
                      </Box>
                    );
                  })()}

                  {!alreadySent && !pendingApproval && (
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={
                        sendLoading ? (
                          <CircularProgress size={16} color='inherit' />
                        ) : needsForeignApproval ? (
                          <HourglassEmptyIcon />
                        ) : (
                          <CallMadeIcon />
                        )
                      }
                      onClick={handleSendResponse}
                      disabled={sendLoading || (needsSelfApproval && !selfApprovalConfirmed)}
                    >
                      {needsForeignApproval ? 'Freigabe anfragen' : 'Antwort senden'}
                    </Button>
                  )}
                </Box>
              )}

              {/* Courtesy voucher summary when resolved */}
              {alreadySent && (selected.courtesyVoucherCode || selected.courtesyVoucherValue) && (
                <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    Kulanzgutschein
                  </Typography>
                  {selected.courtesyVoucherCode && (
                    <Typography variant='body2'>
                      Code: <strong>{selected.courtesyVoucherCode}</strong>
                    </Typography>
                  )}
                  {selected.courtesyVoucherValue && (
                    <Typography variant='body2'>
                      Wert: <strong>{selected.courtesyVoucherValue} €</strong>
                    </Typography>
                  )}
                  {lastApproval?.status === 'approved' && (
                    <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                      Freigegeben
                      {describePerson(lastApproval.decidedBy)
                        ? ` von ${describePerson(lastApproval.decidedBy)}`
                        : ''}
                      {lastApproval.decidedAt ? ` am ${formatDate(lastApproval.decidedAt)}` : ''} ·
                      angefragt
                      {describePerson(lastApproval.requestedBy)
                        ? ` von ${describePerson(lastApproval.requestedBy)}`
                        : ''}
                      {lastApproval.requestedAt ? ` am ${formatDate(lastApproval.requestedAt)}` : ''}
                    </Typography>
                  )}
                </Paper>
              )}
            </Grid>

            {/* Call section — only rendered when a phone number is available */}
            {(selected.customerPhone || selected.bookingId?.phone) && (
            <Grid item xs={12}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle2' sx={{ mb: 1.5 }}>Telefonkontakt</Typography>

                {(() => {
                  const phone = selected.customerPhone || selected.bookingId?.phone;
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                        {phone && (
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<PhoneIcon />}
                            onClick={() => setPhoneVisible(v => !v)}
                            sx={{
                              flex: '1 1 180px',
                              minWidth: 0,
                              maxWidth: '100%',
                              justifyContent: 'flex-start',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {phoneVisible ? phone : 'Nummer anzeigen'}
                          </Button>
                        )}

                        {selected.customerCalled ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label='Angerufen'
                            color='success'
                            size='small'
                            sx={{
                              flex: '0 1 auto',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        ) : (
                          canRespond && (
                            <Button
                              variant='contained'
                              color='success'
                              size='small'
                              startIcon={
                                calledLoading ? (
                                  <CircularProgress size={16} color='inherit' />
                                ) : (
                                  <CheckCircleOutlineIcon />
                                )
                              }
                              onClick={() => setCallNoteDialogOpen(true)}
                              disabled={calledLoading}
                              sx={{ flex: '1 1 140px' }}
                            >
                              Angerufen
                            </Button>
                          )
                        )}
                      </Box>

                      {selected.customerCalled && (
                        <Box>
                          {selected.calledAt && (
                            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                              {formatDate(selected.calledAt)}
                            </Typography>
                          )}
                          {selected.callNote && (
                            <Paper variant='outlined' sx={{ p: 1.5, mb: 1, bgcolor: 'background.default' }}>
                              <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                                Gesprächsnotiz:
                              </Typography>
                              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                {selected.callNote}
                              </Typography>
                            </Paper>
                          )}
                          {canRespond && (
                            <Button
                              variant='text'
                              size='small'
                              color='inherit'
                              onClick={handleUnmarkCalled}
                              disabled={calledLoading}
                              sx={{ color: 'text.secondary', fontSize: '0.72rem' }}
                            >
                              Als nicht angerufen zurücksetzen
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {renderList()}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '4px',
            cursor: 'col-resize',
            bgcolor: 'transparent',
            flexShrink: 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'primary.main',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-2px',
              width: '8px',
              height: '100%',
              cursor: 'col-resize',
            },
          }}
          onMouseDown={handleResizeStart}
        />
        {renderDetail()}
      </Box>

      <CallNoteDialog
        open={callNoteDialogOpen}
        onClose={() => setCallNoteDialogOpen(false)}
        onConfirm={handleCalledConfirm}
        loading={calledLoading}
      />

      <RejectTriageDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectTriageConfirm}
        loading={triageLoading}
      />

      <RejectApprovalDialog
        open={rejectApprovalDialogOpen}
        onClose={() => setRejectApprovalDialogOpen(false)}
        onConfirm={handleRejectVoucherRequest}
        loading={approvalLoading}
        isWithdrawal={!pendingApproval?.canDecide && isOwnApprovalRequest}
      />

      {canReviewAiTriage && (
        <AiTriageReviewDialog
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          items={rejectedItems}
          loading={rejectedLoading}
          onSelect={item => {
            setReviewDialogOpen(false);
            handleSelect(item);
          }}
        />
      )}

      <BookingDetailsModal
        open={!!bookingModalId}
        onClose={() => setBookingModalId(null)}
        bookingId={bookingModalId}
      />

      {/* AI Prompt editor dialog – only shown to users with entertainmentType:editAiPrompt */}
      <Dialog
        open={aiPromptDialogOpen}
        onClose={() => setAiPromptDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>KI-Prompt bearbeiten</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Dieser Prompt wird der KI als zusätzlicher Kontext mitgegeben, wenn du Vorschläge für diesen Entertainment-Typ generierst.
          </Typography>
          <FormControl fullWidth size='small' sx={{ mb: 2 }}>
            <InputLabel id='ai-prompt-type-label'>Entertainment-Typ</InputLabel>
            <Select
              labelId='ai-prompt-type-label'
              value={aiPromptTypeId}
              label='Entertainment-Typ'
              onChange={e => setAiPromptTypeId(e.target.value)}
            >
              {entertainmentTypes.map(et => (
                <MenuItem key={et._id} value={et._id}>{et.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            key={aiPromptTypeId}
            inputRef={aiPromptRef}
            label='KI-Prompt'
            multiline
            minRows={5}
            fullWidth
            defaultValue={entertainmentTypes.find(et => et._id === aiPromptTypeId)?.experienceFeedbackAiPrompt || ''}
            placeholder='z.B. Besonderheiten, typische Beschwerden, spezifische Kulanzregeln für diesen Entertainment-Typ...'
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAiPromptDialogOpen(false)} disabled={aiPromptSaving}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveAiPrompt}
            disabled={aiPromptSaving || !aiPromptTypeId}
            startIcon={aiPromptSaving ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
